/**
 * The parts of a game's look (M19) that need no zod, so the mogzo dev page's bundle of MOGZO's
 * panels stays small. The look's schema and presets are in game-ui.ts.
 */

export const UI_FONTS = ['dm', 'rounded', 'tech', 'classic'] as const;
export const UI_TEXT_SIZES = ['s', 'm', 'l'] as const;
export const UI_SWITCH_SHAPES = ['round', 'square'] as const;
export const UI_SCROLLBARS = ['thin', 'hidden', 'standard'] as const;
export const UI_EDGES = ['top', 'bottom'] as const;
export const UI_PRESET_IDS = ['mogzo-dark', 'mogzo-light', 'neon', 'candy'] as const;
export type UiFont = (typeof UI_FONTS)[number];
export type UiPresetId = (typeof UI_PRESET_IDS)[number];

export const UI_BADGES = ['accent', 'plain'] as const;
export type UiScrollbar = (typeof UI_SCROLLBARS)[number];
export type UiSwitchShape = (typeof UI_SWITCH_SHAPES)[number];
export type UiEdge = (typeof UI_EDGES)[number];

/** Screen sizes a look has its own values for, biggest first. */
export const UI_SIZES = ['computer', 'tablet', 'phone'] as const;
/** The splash in a post, or the launched game. */
export const UI_SCREENS = ['splash', 'game'] as const;
export type UiSize = (typeof UI_SIZES)[number];
export type UiScreen = (typeof UI_SCREENS)[number];
export type UiView = `${UiSize}.${UiScreen}`;
export const UI_VIEWS = UI_SIZES.flatMap((size) =>
  UI_SCREENS.map((screen) => `${size}.${screen}` as const),
) as UiView[];
/** The look everything else starts from. */
export const UI_BASE_VIEW: UiView = 'computer.game';

/** Theme (what every panel starts from) and MOGZO's five panels. */
export const UI_TARGETS = [
  'theme',
  'popup',
  'leaderboard',
  'achievements',
  'pause',
  'signin',
  'match',
] as const;
export type UiTarget = (typeof UI_TARGETS)[number];
export type UiPanel = Exclude<UiTarget, 'theme'>;

/** Corners in pixels, each on its own. */
export interface UiCorners {
  tl: number;
  tr: number;
  br: number;
  bl: number;
}

/** Every value a view can set, for Theme or one panel. */
export interface UiValues {
  /** How much the game is dimmed behind a panel, in percent. */
  dim: number;
  /** Base text size in pixels. */
  textSize: number;
  corners: UiCorners;
  btnRadius: number;
  btnHeight: number;
  pad: number;
  gap: number;
  scrollbar: UiScrollbar;
  switches: UiSwitchShape;
  progress: number;
  edge: UiEdge;
  /** How far in from the edge, to keep the game's own score in view. */
  offset: number;
  width: number;
  /** A phone sheet's height, in percent of the screen. */
  height: number;
  seconds: number;
  rowHeight: number;
  badges: (typeof UI_BADGES)[number];
  iconSize: number;
}
export type UiKey = keyof UiValues;
export type UiNumberKey = { [K in UiKey]: UiValues[K] extends number ? K : never }[UiKey];

/** What Theme and each panel can set; the editor shows only these. */
export const UI_TARGET_KEYS = {
  theme: [
    'dim',
    'textSize',
    'corners',
    'btnRadius',
    'btnHeight',
    'pad',
    'gap',
    'scrollbar',
    'switches',
    'progress',
  ],
  popup: ['edge', 'offset', 'width', 'seconds', 'corners', 'pad', 'textSize'],
  leaderboard: [
    'width',
    'height',
    'corners',
    'pad',
    'textSize',
    'rowHeight',
    'badges',
    'scrollbar',
  ],
  achievements: [
    'width',
    'height',
    'corners',
    'pad',
    'textSize',
    'iconSize',
    'progress',
    'scrollbar',
  ],
  pause: ['width', 'corners', 'btnRadius', 'btnHeight', 'pad', 'gap', 'textSize', 'switches'],
  signin: ['width', 'corners', 'btnRadius', 'btnHeight', 'pad', 'textSize'],
  match: ['width', 'corners', 'btnRadius', 'btnHeight', 'pad', 'gap', 'textSize'],
} as const satisfies Record<UiTarget, readonly UiKey[]>;
export type UiThemeValues = Pick<UiValues, (typeof UI_TARGET_KEYS)['theme'][number]>;

const corners = (n: number): UiCorners => ({ tl: n, tr: n, br: n, bl: n });

/** MOGZO's own values, where every view and panel starts. */
export const UI_BASE = {
  theme: {
    dim: 60,
    textSize: 15,
    corners: corners(18),
    btnRadius: 25,
    btnHeight: 50,
    pad: 18,
    gap: 10,
    scrollbar: 'thin',
    switches: 'round',
    progress: 6,
  } satisfies UiThemeValues,
  popup: { edge: 'top', offset: 0, width: 300, seconds: 4 },
  leaderboard: { width: 290, height: 74, rowHeight: 42, badges: 'accent' },
  achievements: { width: 290, height: 74, iconSize: 38 },
  pause: { width: 330 },
  signin: { width: 320 },
  match: { width: 400 },
} as const satisfies { theme: UiThemeValues } & Record<UiPanel, Partial<UiValues>>;

/**
 * The limits a value must stay in. The slider covers the usual range; the number box takes
 * anything from hardMin to hardMax.
 */
export const UI_LIMITS: Record<
  UiNumberKey,
  { min: number; max: number; hardMin: number; hardMax: number; unit: 'px' | '%' | 's' }
> = {
  dim: { min: 0, max: 90, hardMin: 0, hardMax: 90, unit: '%' },
  textSize: { min: 13, max: 22, hardMin: 13, hardMax: 28, unit: 'px' },
  btnRadius: { min: 0, max: 36, hardMin: 0, hardMax: 48, unit: 'px' },
  btnHeight: { min: 44, max: 72, hardMin: 44, hardMax: 96, unit: 'px' },
  pad: { min: 8, max: 32, hardMin: 4, hardMax: 48, unit: 'px' },
  gap: { min: 2, max: 24, hardMin: 0, hardMax: 40, unit: 'px' },
  progress: { min: 2, max: 16, hardMin: 2, hardMax: 24, unit: 'px' },
  offset: { min: 0, max: 160, hardMin: 0, hardMax: 400, unit: 'px' },
  width: { min: 220, max: 440, hardMin: 200, hardMax: 640, unit: 'px' },
  height: { min: 50, max: 92, hardMin: 40, hardMax: 96, unit: '%' },
  seconds: { min: 3, max: 8, hardMin: 3, hardMax: 10, unit: 's' },
  rowHeight: { min: 32, max: 64, hardMin: 28, hardMax: 96, unit: 'px' },
  iconSize: { min: 28, max: 56, hardMin: 24, hardMax: 72, unit: 'px' },
};
/** Each corner, in pixels. */
export const UI_CORNER_MAX = 64;

/** What one view sets: for Theme and each panel, only the values it changes. */
export type UiViewValues = { [T in UiTarget]?: Partial<UiValues> };
export type UiViews = { [V in UiView]?: UiViewValues };

/**
 * Where a view takes the values it doesn't set: a splash from its size's game, then the bigger
 * sizes' game, ending at Computer · Game.
 */
export function viewChain(view: UiView): UiView[] {
  const [size, screen] = view.split('.') as [UiSize, UiScreen];
  const chain: UiView[] = screen === 'splash' ? [view] : [];
  for (let i = UI_SIZES.indexOf(size); i >= 0; i--) chain.push(`${UI_SIZES[i] as UiSize}.game`);
  return chain;
}

/** A value and where it came from: a view (null for MOGZO's own) and Theme or the panel. */
export interface UiResolved<K extends UiKey> {
  value: UiValues[K];
  view: UiView | null;
  target: UiTarget;
}

const THEME_KEYS: readonly UiKey[] = UI_TARGET_KEYS.theme;

/**
 * One value for a panel in a view: the panel's own (searching up the views), then Theme's, then
 * MOGZO's.
 */
export function resolveValue<K extends UiKey>(
  views: UiViews,
  view: UiView,
  target: UiTarget,
  key: K,
): UiResolved<K> {
  const chain = viewChain(view);
  const set = (v: UiView, t: UiTarget) => views[v]?.[t]?.[key] as UiValues[K] | undefined;
  for (const v of chain) {
    const value = set(v, target);
    if (value !== undefined) return { value, view: v, target };
  }
  if (THEME_KEYS.includes(key)) {
    for (const v of chain) {
      const value = set(v, 'theme');
      if (value !== undefined) return { value, view: v, target: 'theme' };
    }
    return {
      value: UI_BASE.theme[key as keyof UiThemeValues] as UiValues[K],
      view: null,
      target: 'theme',
    };
  }
  const own = (UI_BASE[target] as Partial<UiValues>)[key];
  return { value: own as UiValues[K], view: null, target };
}

/** Every value a panel (or Theme) draws with in a view. */
export function resolveValues(views: UiViews, view: UiView, target: UiTarget): UiValues {
  const out: Partial<Record<UiKey, unknown>> = {};
  for (const key of UI_KEYS) {
    // A value only other panels have (a leaderboard's icon size) comes from MOGZO's.
    out[key] = resolveValue(views, view, target, key).value ?? fallbackValue(key);
  }
  return out as UiValues;
}

const UI_KEYS = [...new Set(Object.values(UI_TARGET_KEYS).flat())] as UiKey[];

function fallbackValue(key: UiKey): unknown {
  for (const panel of UI_TARGETS) {
    const value = (UI_BASE[panel] as Partial<UiValues>)[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

/** How many values a view sets, for one target or all. */
export function countSet(views: UiViews, view: UiView, target?: UiTarget): number {
  const set = views[view] ?? {};
  const targets = target ? [target] : (Object.keys(set) as UiTarget[]);
  return targets.reduce((n, t) => n + Object.keys(set[t] ?? {}).length, 0);
}

/** Fonts players' devices get from mogzo.com (DM Sans and Fredoka are mogzo.com's own). */
export const UI_FONT_NAMES: Record<UiFont, string> = {
  dm: 'MOGZO (DM Sans)',
  rounded: 'Rounded (Fredoka)',
  tech: 'Tech (Chakra Petch)',
  classic: 'Classic (Lora)',
};
export const UI_FONT_STACKS: Record<UiFont, string> = {
  dm: "'DM Sans Variable', 'DM Sans', system-ui, sans-serif",
  rounded: "Fredoka, 'DM Sans Variable', system-ui, sans-serif",
  tech: "'Chakra Petch', 'DM Sans Variable', system-ui, sans-serif",
  classic: 'Lora, Georgia, serif',
};
/** Text sizes of looks saved before per-view values, in pixels. */
export const UI_TEXT_PX: Record<(typeof UI_TEXT_SIZES)[number], number> = { s: 14, m: 15, l: 17 };

/** Text must stand out this much from what's behind it (WCAG AA). */
export const UI_CONTRAST_MIN = 4.5;

export interface UiColours {
  panel: string;
  text: string;
  muted: string;
  accent: string;
  onAccent: string;
  field: string;
  line: string;
}

function luminance(hex: string): number {
  const n = Number.parseInt(hex.slice(1), 16);
  const [r, g, b] = [n >> 16, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** How much two colours stand apart, from 1 (the same) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** The pairs players read: each must reach UI_CONTRAST_MIN. */
export function uiContrastChecks(colours: UiColours) {
  return (
    [
      ['text', 'Text on panels', colours.text, colours.panel],
      ['muted', 'Quiet text on panels', colours.muted, colours.panel],
      ['onAccent', 'Text on buttons', colours.onAccent, colours.accent],
    ] as const
  ).map(([key, label, front, back]) => {
    const ratio = contrastRatio(front, back);
    return { key, label, ratio, ok: ratio >= UI_CONTRAST_MIN };
  });
}

/** A player's own choices in the pause menu, kept per game on their device. */
export interface GameSettings {
  sound: boolean;
}
